/**
 * Tailwind exports: utilities point to CSS variables, so both themes work without rebuilding.
 * - `tailwindPreset`: JS preset (Tailwind 3 style `presets: [...]`).
 * - `tailwindThemeCss()`: Tailwind 4 `@theme inline` block (dist/tailwind-theme.css), for the
 *   public website (`@import "@agencyos/design-tokens/tailwind-theme.css"` after tokens.css).
 */

import { GLASS_MATERIALS } from "./css";
import { SPACE_STEPS, radius, themes, typography } from "./tokens";

const kebab = (key: string): string => key.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);
const v = (name: string): string => `var(--ao-${name})`;

const COLOR_KEYS = Object.keys(themes.light.color);
const RADII: [string, number][] = Object.entries(radius).flatMap(([key, value]) =>
  typeof value === "number" ? [[key, value] as [string, number]] : [],
);

export const tailwindPreset = {
  darkMode: ["selector", '[data-theme="dark"]'],
  theme: {
    extend: {
      colors: {
        ...Object.fromEntries(COLOR_KEYS.map((key) => [kebab(key), v(kebab(key))])),
        brand: { 600: v("brand600"), 900: v("brand900") },
        accent: { DEFAULT: v("accent"), red: v("accent-red"), amber: v("accent-amber") },
        surface: { DEFAULT: v("surface"), alt: v("surface-alt") },
      },
      fontFamily: {
        heading: [typography.fontHeading],
        body: [typography.fontBody],
        mono: [typography.fontMono],
      },
      fontSize: Object.fromEntries(
        Object.entries(typography.web).map(([role, s]) => [
          kebab(role),
          [`${s.size}px`, { lineHeight: `${s.lineHeight}px`, letterSpacing: `${s.tracking}em`, fontWeight: String(s.weight) }],
        ]),
      ),
      borderRadius: Object.fromEntries(RADII.map(([key]) => [key, v(`radius-${key}`)])),
      boxShadow: Object.fromEntries(["xs", "sm", "md", "lg", "xl"].map((k) => [`ao-${k}`, v(`shadow-${k}`)])),
      transitionTimingFunction: {
        "out-expo": v("ease-out-expo"),
        standard: v("ease-standard"),
        "spring-snappy": v("spring-snappy"),
        "spring-smooth": v("spring-smooth"),
        "spring-bouncy": v("spring-bouncy"),
      },
    },
  },
} as const;

/** Tailwind 4: `@theme inline` so utilities keep reading the live (themed) variables. */
export function tailwindThemeCss(): string {
  const lines: string[] = [];
  for (const key of COLOR_KEYS) lines.push(`  --color-${kebab(key)}: ${v(kebab(key))};`);
  for (const level of GLASS_MATERIALS) lines.push(`  --color-glass-${kebab(level)}: ${v(`glass-${kebab(level)}-bg`)};`);
  lines.push(`  --font-heading: ${v("font-heading")};`, `  --font-body: ${v("font-body")};`, `  --font-mono: ${v("font-mono")};`);
  for (const [role, s] of Object.entries(typography.web)) {
    const name = kebab(role);
    lines.push(
      `  --text-${name}: ${s.size}px;`,
      `  --text-${name}--line-height: ${s.lineHeight}px;`,
      `  --text-${name}--letter-spacing: ${s.tracking}em;`,
      `  --text-${name}--font-weight: ${s.weight};`,
    );
  }
  for (const [key] of RADII) lines.push(`  --radius-${key}: ${v(`radius-${key}`)};`);
  for (const k of ["xs", "sm", "md", "lg", "xl"]) lines.push(`  --shadow-ao-${k}: ${v(`shadow-${k}`)};`);
  for (const step of SPACE_STEPS) lines.push(`  --spacing-ao-${step}: ${v(`space-${step}`)};`);
  lines.push(`  --ease-spring-snappy: ${v("spring-snappy")};`, `  --ease-spring-smooth: ${v("spring-smooth")};`);
  return `/* Generated by @agencyos/design-tokens — do not edit by hand. Tailwind 4 theme. */\n@theme inline {\n${lines.join("\n")}\n}\n`;
}
